import { useMediaQuery } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { Button } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import useResultContext from '../../../features/dashboard/store/UseResultContext';
import i18n from '../../../i18n';
import useOrgUnitLogo from '../../hooks/use-orgunit-logo';
import Events from '../../utils/events';
import GLTBreadcrumb from '../glt-breadcrumb/GLTBreadcrumb';
import DashboardNavigationLinks from './navigation/dashboardNavigation/DashboardNavigation';
import HeaderNavigation from './navigation/headerNavigation/HeaderNavigation';

import './HeaderBar.scss';

const HeaderBar = () => {
  const { t } = useTranslation();
  const { bemClassName, bemClassNames } = useBem('header-bar');
  const isSmallDevice = useMediaQuery('only screen and (max-width : 1270px)');
  const { result } = useResultContext();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const hasResults = result.responseCode;
  const showBreadcrumb = window.location.pathname !== '/dashboard';

  const imageUrl = useOrgUnitLogo();

  const setLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    window.dispatchEvent(new Event(Events.LOCAL_STORAGE_UPDATE_LANGUAGE));
    document.documentElement.lang = lang;
  };

  const currentLang = i18n.language;

  const languageToggle = () => (
    <div
      className={bemClassNames([bemClassName('menu-item'), bemClassName('menu-item', 'language')])}
      data-active-lang={currentLang}
    >
      <Button
        onClick={() => setLanguage('nl')}
        data-active={currentLang === 'nl'}
      >
        {t('generic.menu.language.dutch')!}
      </Button>
      <Button
        onClick={() => setLanguage('en')}
        data-active={currentLang === 'en'}
      >
        {t('generic.menu.language.english')!}
      </Button>
    </div>
  );

  return (
    <header className={bemClassName()}>
      <div className={bemClassName('left-side')}>
        {hasResults ? (
          <NavLink
            to="/dashboard"
            className={bemClassName('title')}
          >
            {imageUrl && (
              <img
                src={`${window.__RUNTIME_CONFIG__.ODATA_URL}/${imageUrl}`}
                alt=""
                height={24}
              />
            )}
            <span>{t('generic.title')}</span>
          </NavLink>
        ) : (
          <div className={bemClassName('title')}>
            {imageUrl && (
              <img
                src={`${window.__RUNTIME_CONFIG__.ODATA_URL}/${imageUrl}`}
                alt=""
                height={24}
              />
            )}
            <span>{t('generic.title')}</span>
          </div>
        )}
        {isSmallDevice && languageToggle()}
      </div>
      <div className={bemClassName('right-side')}>
        {isSmallDevice && (
          <div
            className={bemClassNames([
              bemClassName('mobile-menu-container'),
              bemClassName('mobile-menu-container', 'active', mobileMenuOpen),
            ])}
          >
            {hasResults && !mobileMenuOpen && (
              <GLTBreadcrumb
                className={bemClassName('mobile-breadcrumb')}
                visible={showBreadcrumb}
              />
            )}
            <div
              className={bemClassNames([
                bemClassName('menu-mobile'),
                bemClassName('menu-mobile', 'active', mobileMenuOpen),
              ])}
            >
              <button
                className="hamburger-lines"
                onClick={() => toggleMobileMenu()}
                type="button"
                title="hamburger menu"
              >
                <span className="line line1" />
                <span className="line line2" />
                <span className="line line3" />
              </button>
            </div>
            {mobileMenuOpen && (
              <nav className={bemClassName('menu')}>
                <div className={bemClassName('mobile-nav-column')}>
                  {hasResults && <DashboardNavigationLinks />}
                  <div className={bemClassName('section-group')}>
                    <HeaderNavigation isMobile />
                  </div>
                </div>
              </nav>
            )}
          </div>
        )}
        {!isSmallDevice && (
          <>
            <HeaderNavigation />
            {languageToggle()}
          </>
        )}
      </div>
    </header>
  );
};

export default HeaderBar;
